import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Bell, 
  Car, 
  User, 
  FileText,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente Card Offerta Compatta (fuori dal componente principale)
const OfferCard = ({ offer, onNavigate }) => (
  <div 
    className="p-3 bg-white rounded-lg border border-violet-200 hover:border-violet-400 transition-colors cursor-pointer"
    onClick={() => onNavigate(offer.id)}
    data-testid={`pending-offer-${offer.id}`}
  >
    <div className="flex items-center gap-3">
      {/* Info Veicolo */}
      <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
        <Car className="w-5 h-5 text-violet-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-slate-800 text-sm">
            {offer.vehicle_info?.marca} {offer.vehicle_info?.modello}
          </p>
          <Badge className="bg-violet-100 text-violet-700 text-[10px]">
            ★ Accettato
          </Badge>
        </div>
        <p className="text-xs text-slate-500">
          {offer.contact_info?.nome_cognome || 'Cliente'} • €{offer.canone_finale?.toLocaleString('it-IT')}/mese
        </p>
      </div>
      
      {/* Info Accettazione */}
      <div className="text-right">
        <p className="text-xs text-slate-500">
          {new Date(offer.accepted_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
        </p>
        <ArrowRight className="w-4 h-4 text-violet-400 ml-auto mt-1" />
      </div>
    </div>
  </div>
);

const OfferteAttivePage = () => {
  const { getAuthHeader, user, getImpersonateParams, impersonatedUser } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAgents, setExpandedAgents] = useState({});

  const isAdmin = ['admin', 'super_admin', 'top_agent'].includes(user?.role);

  const fetchPendingDocs = useCallback(async () => {
    try {
      // Passa view_as se Admin sta impersonando un utente
      const impersonateParams = getImpersonateParams();
      const queryParams = new URLSearchParams(impersonateParams);
      const queryString = queryParams.toString();
      const url = queryString ? `${API}/offers/pending-docs?${queryString}` : `${API}/offers/pending-docs`;
      
      const response = await axios.get(url, { 
        headers: getAuthHeader() 
      });
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error('Error fetching pending docs:', error);
      toast.error('Errore nel caricamento delle offerte');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, getImpersonateParams]);

  useEffect(() => {
    fetchPendingDocs();
  }, [fetchPendingDocs]);

  // Raggruppa offerte per agente (solo per Admin)
  const offersByAgent = isAdmin 
    ? offers.reduce((acc, offer) => {
        const agentId = offer.user_id;
        const agentName = offer.agent_name || offer.user_name || 'Agente';
        if (!acc[agentId]) {
          acc[agentId] = {
            name: agentName,
            email: offer.agent_email,
            offers: []
          };
        }
        acc[agentId].offers.push(offer);
        return acc;
      }, {})
    : null;

  const toggleAgent = (agentId) => {
    setExpandedAgents(prev => ({
      ...prev,
      [agentId]: !prev[agentId]
    }));
  };

  // Naviga alle offerte con filtro per offerta specifica
  const goToOffer = (offerId) => {
    navigate(`/dashboard/offerte?highlight=${offerId}`);
  };

  // Naviga alle offerte (lista generale)
  const goToOffers = () => {
    navigate('/dashboard/offerte');
  };

  return (
    <div className="space-y-6" data-testid="offerte-attive-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800 flex items-center gap-3" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            <Bell className="w-7 h-7 text-violet-600" />
            Offerte Accettate
            {offers.length > 0 && (
              <Badge className="bg-violet-600 text-white text-lg px-3 py-1">
                {offers.length}
              </Badge>
            )}
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin 
              ? 'Offerte accettate dai clienti - clicca per gestire l\'esito'
              : 'I tuoi clienti hanno accettato queste offerte - gestisci gli esiti'}
          </p>
        </div>
        <Button
          onClick={goToOffers}
          variant="outline"
          className="text-violet-600 border-violet-200 hover:bg-violet-50"
        >
          <FileText className="w-4 h-4 mr-2" />
          Vai a Tutte le Offerte
        </Button>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-violet-800 font-medium">
              Queste offerte sono state accettate dai clienti
            </p>
            <p className="text-xs text-violet-600 mt-1">
              Clicca su un&apos;offerta per aprirla nella pagina Offerte e gestire l&apos;esito. 
              Le offerte accettate appaiono in cima alla lista con bordo viola.
            </p>
          </div>
        </div>
      </div>

      {/* Contenuto */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : offers.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">Nessuna offerta accettata</p>
              <p className="text-sm text-slate-400 mt-1">
                Quando un cliente accetterà un&apos;offerta, apparirà qui.
              </p>
              <Button
                onClick={goToOffers}
                variant="outline"
                className="mt-4"
              >
                Vai alle Offerte
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : isAdmin ? (
        /* Vista Admin - Raggruppata per agente */
        <div className="space-y-4">
          {Object.entries(offersByAgent).map(([agentId, agentData]) => (
            <Card key={agentId} className="overflow-hidden border-violet-200">
              <CardHeader 
                className="cursor-pointer bg-violet-50/50 hover:bg-violet-50 transition-colors py-3"
                onClick={() => toggleAgent(agentId)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{agentData.name}</p>
                      {agentData.email && (
                        <p className="text-xs text-slate-500">{agentData.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-violet-600 text-white text-xs">
                      {agentData.offers.length} offert{agentData.offers.length === 1 ? 'a' : 'e'}
                    </Badge>
                    {expandedAgents[agentId] ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              
              {expandedAgents[agentId] && (
                <CardContent className="pt-3 pb-3">
                  <div className="space-y-2">
                    {agentData.offers.map(offer => (
                      <OfferCard key={offer.id} offer={offer} onNavigate={goToOffer} />
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      ) : (
        /* Vista Agente - Lista semplice */
        <Card className="border-violet-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5 text-violet-600" />
              In Attesa di Gestione ({offers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {offers.map(offer => (
                <OfferCard key={offer.id} offer={offer} onNavigate={goToOffer} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OfferteAttivePage;
